import { AuthenticationUser, useRefreshTokenMutation } from '@codezo/graphql';
import { AuthLoadingScreen } from './AuthLoadingScreen';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { devError } from '../utils/devLog';
import { scheduleTokenRefresh } from '../utils/tokenScheduler';
import { storeUserRole } from '../utils/tokenStorage';

interface ReAuthenticateCallbackProps {
	setUser: (user: AuthenticationUser) => void;
	setAccessToken: (token: string) => void;
	/**
	 * Where to send a session that couldn't be restored, when the public login
	 * page is the wrong answer — password-less guest sessions have no account
	 * to sign back into and need their own recovery route. Return an in-app
	 * path, or `null` for the default logout redirect.
	 */
	resolveFailureRedirect?: () => string | null;
}

// Global flag to prevent multiple simultaneous re-auth attempts
let isReAuthenticating = false;

export function ReAuthenticateCallback({
	setUser,
	setAccessToken,
	resolveFailureRedirect,
}: ReAuthenticateCallbackProps) {
	const navigate = useNavigate();
	const hasRun = useRef(false);

	const hash = location.hash.substring(1);
	const prevPath = new URLSearchParams(hash).get('prevPath');

	/** Single exit for every failure mode: app-supplied recovery, else logout. */
	const bailOut = () => {
		const fallback = resolveFailureRedirect?.();
		if (fallback) {
			window.location.replace(fallback);
			return;
		}
		const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '';
		window.location.replace(`${publicAppUrl}/auth/login?logout=true`);
	};

	const [refreshTokenMutation] = useRefreshTokenMutation({
		onError: (err) => {
			devError('Re-auth: refresh mutation failed', err);
			bailOut();
		},
	});

	useEffect(() => {
		// Two-layer dedup: hasRun guards same-instance re-renders (effect re-fires
		// when deps change), isReAuthenticating guards remounts triggered by the
		// post-success setUser/navigate cycle.
		if (hasRun.current || isReAuthenticating) {
			return;
		}

		hasRun.current = true;
		isReAuthenticating = true;

		const attemptReAuthentication = async () => {
			try {
				const result = await refreshTokenMutation();

				if (result.data?.refreshToken) {
					const { user, token } = result.data.refreshToken;

					setUser(user as AuthenticationUser);
					setAccessToken(token);
					storeUserRole(user.role);
					scheduleTokenRefresh(token);

					navigate(prevPath || '/dashboard', { replace: true });
				} else {
					bailOut();
				}
			} catch (err) {
				devError('Re-auth: unexpected exception', err);
				bailOut();
			} finally {
				// Reset the flag so a future legitimate re-auth attempt isn't permanently blocked
				// (e.g. if the user logs out and back in within the same SPA lifecycle).
				isReAuthenticating = false;
			}
		};

		attemptReAuthentication();
	}, [refreshTokenMutation, setUser, setAccessToken, navigate, prevPath]);

	return (
		<AuthLoadingScreen
			title="Refreshing your session..."
			description="Please wait while we restore your access."
		/>
	);
}
